/**
 * IdentitySelect — identidad de ejecución del editor de rutinas
 * (REQ-FRT-004; revisión UI del 18-Oct-2026).
 *
 * - Usuario: por defecto el propio; listar o buscar otros usuarios exige
 *   permiso de gestión de usuarios (el backend lo reimpone fail-closed). Se
 *   muestra nombre/email, nunca identificadores internos.
 * - Cuenta de servicio: búsqueda por nombre (admin del tenant: todas; resto:
 *   solo las propias, regla del módulo de cuentas de servicio).
 */

import { useEffect, useMemo, useState } from 'react';
import { Icon, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useAuth } from '@/lib/auth';
import { useServiceAccounts } from '@/hooks/use-service-accounts';
import { useTenantMembers } from '@/features/permissions/hooks/usePermissions';

export interface RunAsValue {
  mode: 'user' | 'service_account';
  subject_id: string;
}

export interface IdentitySelectProps {
  value: RunAsValue;
  onChange: (next: RunAsValue) => void;
}

export function IdentitySelect({ value, onChange }: IdentitySelectProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const members = useTenantMembers();
  const [userQuery, setUserQuery] = useState('');
  const [saQuery, setSaQuery] = useState('');
  const [saSearch, setSaSearch] = useState('');

  // Debounce the service-account server-side search (REQ-FRT-004).
  useEffect(() => {
    const timer = setTimeout(() => setSaSearch(saQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [saQuery]);

  const serviceAccounts = useServiceAccounts({
    page_size: 100,
    search: saSearch || undefined,
  });

  const canPickOthers = members.isSuccess;
  const selfLabel = user?.name ? `${user.name} (${user.email})` : (user?.email ?? user?.id ?? '');

  const visibleMembers = useMemo(() => {
    const items = members.data?.items ?? [];
    const normalized = userQuery.trim().toLowerCase();
    return items
      .filter((member) =>
        !normalized
          ? true
          : (member.name ?? '').toLowerCase().includes(normalized) ||
            (member.email ?? '').toLowerCase().includes(normalized),
      )
      .slice(0, 8);
  }, [members.data, userQuery]);

  const selectedMember = (members.data?.items ?? []).find(
    (member) => member.user_id === value.subject_id,
  );

  const setMode = (mode: RunAsValue['mode']) => {
    if (mode === 'user') {
      onChange({ mode, subject_id: user?.id ?? '' });
      return;
    }
    onChange({ mode, subject_id: '' });
  };

  return (
    <div className="space-y-3" data-testid="identity-select">
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="identity-mode-user"
          aria-pressed={value.mode === 'user'}
          onClick={() => setMode('user')}
          className={`rounded-lg border px-3 py-1.5 text-sm ${
            value.mode === 'user'
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200'
              : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
          }`}
        >
          {t('frontier.editor.identityUser')}
        </button>
        <button
          type="button"
          data-testid="identity-mode-service-account"
          aria-pressed={value.mode === 'service_account'}
          onClick={() => setMode('service_account')}
          className={`rounded-lg border px-3 py-1.5 text-sm ${
            value.mode === 'service_account'
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200'
              : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
          }`}
        >
          {t('frontier.editor.identityServiceAccount')}
        </button>
      </div>

      {value.mode === 'user' && canPickOthers && (
        <div className="space-y-2">
          <input
            type="search"
            data-testid="identity-user-search"
            value={userQuery}
            onChange={(event) => setUserQuery(event.target.value)}
            placeholder={t('frontier.editor.identitySearchUser')}
            aria-label={t('frontier.editor.identitySearchUser')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
          />
          <div className="max-h-44 space-y-1 overflow-y-auto">
            <button
              type="button"
              data-testid="identity-user-self"
              onClick={() => onChange({ mode: 'user', subject_id: user?.id ?? '' })}
              className={`flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                value.subject_id === user?.id ? 'bg-blue-50 dark:bg-blue-950' : ''
              }`}
            >
              <Icon name="users" size={16} className="text-gray-400" />
              {t('frontier.editor.identitySelf', { user: selfLabel })}
            </button>
            {visibleMembers
              .filter((member) => member.user_id !== user?.id)
              .map((member) => (
                <button
                  key={member.user_id}
                  type="button"
                  data-testid={`identity-user-${member.user_id}`}
                  onClick={() => onChange({ mode: 'user', subject_id: member.user_id })}
                  className={`flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    value.subject_id === member.user_id
                      ? 'bg-blue-50 dark:bg-blue-950'
                      : ''
                  }`}
                >
                  <Icon name="users" size={16} className="text-gray-400" />
                  <span className="min-w-0 flex-1 truncate">
                    {member.name ?? member.email ?? member.user_id}
                    {member.email && member.name ? (
                      <span className="text-gray-500"> · {member.email}</span>
                    ) : null}
                  </span>
                </button>
              ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('frontier.editor.identityUserHint')}
          </p>
        </div>
      )}

      {value.mode === 'user' && !canPickOthers && (
        <div className="space-y-1" data-testid="identity-user-self-only">
          {members.isLoading ? (
            <Spinner size="sm" />
          ) : (
            <p className="text-sm font-medium">
              {t('frontier.editor.identitySelf', { user: selfLabel })}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('frontier.editor.identitySelfOnly')}
          </p>
        </div>
      )}

      {value.mode === 'user' && canPickOthers && selectedMember && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {selectedMember.name ?? selectedMember.email}
        </p>
      )}

      {value.mode === 'service_account' && (
        <div className="space-y-2">
          <input
            type="search"
            data-testid="identity-sa-search"
            value={saQuery}
            onChange={(event) => setSaQuery(event.target.value)}
            placeholder={t('frontier.editor.saSearch')}
            aria-label={t('frontier.editor.saSearch')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
          />
          <div className="max-h-44 space-y-1 overflow-y-auto">
            {serviceAccounts.isLoading ? (
              <Spinner size="sm" />
            ) : (serviceAccounts.data?.data ?? []).length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('frontier.editor.saEmpty')}
              </p>
            ) : (
              (serviceAccounts.data?.data ?? []).map((account) => (
                <button
                  key={account.id}
                  type="button"
                  data-testid={`identity-sa-${account.id}`}
                  disabled={account.status !== 'active'}
                  onClick={() =>
                    onChange({ mode: 'service_account', subject_id: account.client_id })
                  }
                  className={`flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-gray-800 ${
                    value.subject_id === account.client_id
                      ? 'bg-blue-50 dark:bg-blue-950'
                      : ''
                  }`}
                >
                  <Icon name="shield" size={16} className="text-gray-400" />
                  <span className="min-w-0 flex-1 truncate">{account.name}</span>
                  <span className="text-xs text-gray-500">{account.status}</span>
                </button>
              ))
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('frontier.editor.saHint')}
          </p>
        </div>
      )}
    </div>
  );
}
